import { useQuery } from '@tanstack/react-query';
import { Layer, Source } from '@vis.gl/react-maplibre';
import type { DataDrivenPropertyValueSpecification } from 'maplibre-gl';
import { Fragment } from 'react';

import AlternativeRoutingIcon from '@/assets/icons/map/traffic-report/alternative-routing.svg?url';
import BarrierIcon from '@/assets/icons/map/traffic-report/barrier.svg?url';
import ConstructionWorksIcon from '@/assets/icons/map/traffic-report/construction-works.svg?url';
import DiversionIcon from '@/assets/icons/map/traffic-report/diversion.svg?url';
import EnvironmentalObstructionIcon from '@/assets/icons/map/traffic-report/environmental-obstruction.svg?url';
import MaintenceWorksIcon from '@/assets/icons/map/traffic-report/maintenance-works.svg?url';
import NaturalEventIcon from '@/assets/icons/map/traffic-report/natural-event.svg?url';
import PublicEventIcon from '@/assets/icons/map/traffic-report/public-event.svg?url';
import TrafficJamIcon from '@/assets/icons/map/traffic-report/traffic-jam.svg?url';
import UnassignableIcon from '@/assets/icons/map/traffic-report/unassignable.svg?url';
import testTrafficReportsData from '@/data/testTrafficReports.json';
import { useMapImages } from '@/hooks/useMapImages';
import { FeatureCollectionToCenterOfMass } from '@/modules/FeatureCollectionToCenterOfMass';
import { GeoServer } from '@/modules/GeoServer';
import { ICON_SIZE } from '@/modules/IconSize';
import { MapLayer } from '@/types/MapLayer';
import { TrafficReportType } from '@/types/TrafficReportType';

const ICON_DIMENSIONS = { width: 36, height: 36 };

export function TrafficReportLayer() {
  useMapImages({
    images: [
      { name: TrafficReportType.ALTERNATIVE_ROUTING, url: AlternativeRoutingIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.BARRIER, url: BarrierIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.CONSTRUCTION_WORKS, url: ConstructionWorksIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.DIVERSION, url: DiversionIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.ENVIRONMENTAL_OBSTRUCTION, url: EnvironmentalObstructionIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.MAINTENANCE_WORKS, url: MaintenceWorksIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.NATURAL_EVENT, url: NaturalEventIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.PUBLIC_EVENT, url: PublicEventIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.TRAFFIC_JAM, url: TrafficJamIcon, ...ICON_DIMENSIONS },
      { name: TrafficReportType.UNASSIGNABLE, url: UnassignableIcon, ...ICON_DIMENSIONS },
    ],
  });

  const { data: trafficReport } = useQuery({
    queryKey: [MapLayer.TRAFFIC_REPORT],
    queryFn: async () =>
      FeatureCollectionToCenterOfMass.fetch(GeoServer.wfs({ typeName: 'traffic_obstruction_geoms' })),
  });

  return (
    <Fragment>
      <Source
        key={MapLayer.TRAFFIC_REPORT}
        data={trafficReport ?? { type: 'FeatureCollection', features: [] }}
        id={MapLayer.TRAFFIC_REPORT}
        promoteId="id"
        type="geojson"
      >
        <Layer
          key={MapLayer.TRAFFIC_REPORT}
          id={MapLayer.TRAFFIC_REPORT}
          type="symbol"
          layout={{
            'icon-size': ICON_SIZE,
            'icon-allow-overlap': true,
            'icon-image': [
              'match',
              ['get', 'type'],
              ...Object.values(TrafficReportType)
                .map((value) => [value, value])
                .flat(),
              TrafficReportType.UNASSIGNABLE,
            ] as unknown as DataDrivenPropertyValueSpecification<string>,
          }}
        />
      </Source>

      {/* Test Layer mit 10 Reports im Kreis */}
      <Source
        key="TRAFFIC_REPORT_TEST"
        data={testTrafficReportsData as GeoJSON.FeatureCollection}
        id="TRAFFIC_REPORT_TEST"
        promoteId="id"
        type="geojson"
      >
        <Layer
          key="TRAFFIC_REPORT_TEST"
          id="TRAFFIC_REPORT_TEST"
          type="symbol"
          layout={{
            'icon-size': ICON_SIZE,
            'icon-allow-overlap': true,
            'icon-image': [
              'match',
              ['get', 'type'],
              ...Object.values(TrafficReportType)
                .map((value) => [value, value])
                .flat(),
              TrafficReportType.UNASSIGNABLE,
            ] as unknown as DataDrivenPropertyValueSpecification<string>,
          }}
        />
      </Source>
    </Fragment>
  );
}
